import {Component} from '@angular/core';
import {
  AlertController,
  MenuController,
  ModalController,
  NavController,
  Platform,
} from '@ionic/angular';
import {Router} from '@angular/router';
import {Browser} from '@capacitor/browser';
import {App as CapacitorApp} from '@capacitor/app';
import {StatusBar, Style} from '@capacitor/status-bar';
import {EdgeToEdge} from '@capawesome/capacitor-android-edge-to-edge-support';
import * as LiveUpdates from '@capacitor/live-updates';
import {AuthService} from './services/auth.service';
import {SplashScreen} from '@capacitor/splash-screen';
import {AppService} from './services/app.service';
import {Location} from '@angular/common';
import {StorageService} from './services/storage.service';
import {OrderCompletedComponent} from './components/order-completed/order-completed.component';
import {LoggerService} from './services/logger.service';
import {AdAttributionService} from './services/ad-attribution.service';
import {environment} from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent {
  step: number;
  token: any;
  handlerMessage = '';
  roleMessage = '';
  user_group = 3;
  user: any = {};

  constructor(
    private platform: Platform,
    private location: Location,
    private menu: MenuController,
    private navCtrl: NavController,
    private modalController: ModalController,
    private router: Router,
    private alertCtrl: AlertController,
    private storage: StorageService,
    private app: AppService,
    private authService: AuthService,
    private loggerService: LoggerService,
    private adAttribution: AdAttributionService
  ) {}

  ngOnInit() {
    this.initializeApp();
    this.backbtn();
    window.onerror = (error: any) => {
      this.loggerService.logErrorAndAlert(error);
    };
  }

  initializeApp() {
    try {
      this.platform.ready().then(async () => {
        // Initialize StatusBar - use EdgeToEdge for Android status bar, StatusBar for iOS
        if (this.platform.is('capacitor')) {
          if (this.platform.is('android')) {
            // Use EdgeToEdge plugin for Android to set status bar background color
            await EdgeToEdge.enable();
            await EdgeToEdge.setBackgroundColor({color: '#414141'});
            await StatusBar.setStyle({style: Style.Light});
          } else if (this.platform.is('ios')) {
            // Use standard StatusBar for iOS
            await StatusBar.setStyle({style: Style.Light});
            await StatusBar.setBackgroundColor({color: '#414141'});
          }

          // Live updates from AppFlow (production builds only).
          // Apply silently at cold launch, behind the splash, before the user
          // interacts. autoUpdateMethod is 'none' so the plugin never applies
          // an update mid-session (e.g. on resume from reading an SMS OTP),
          // which used to reload the webview and disrupt the signup flow.
          if (environment.production) {
            try {
              const result = await LiveUpdates.sync();
              if (result.activeApplicationPathChanged) {
                await LiveUpdates.reload();
              }
            } catch (e) {
              this.loggerService.logError(`Live update sync error: ${e.message}`);
            }
          }
        }
        
        await SplashScreen.hide();
        await this.storage.init();
        // Android-only, first-launch-only: reads the Play Install Referrer
        // for a gclid/gbraid/wbraid from a Google Ads-driven install.
        this.adAttribution.captureFromInstallReferrer().catch(() => {});
        await this.authService.getUserAsync();
        this.checkAndRedirect();
        this.authService.authenticationState.subscribe((isAuthenticated) => {
          const currentUrl = window.location.href;

          if (
            currentUrl.includes('/buildquotelogin') &&
            !currentUrl.includes('buildquotelogin;data=')
          ) {
            const match = currentUrl.match(/\/buildquotelogin(.*)/);

            if (match) {
              const baseUrl = match[0];
              const queryParams = match[1];
              this.router.navigate(['/buildquotelogin', {data: queryParams}]);
            } else {
            }
          } else {
            if (isAuthenticated) {
              this.authService.userInfo.subscribe((userRes) => {
                this.user = userRes;
                this.user_group = this.user.user_group;
              });
              //this.loggerService.logInformation('preonsignalinit');
              // this.authService.oneSignalInit();
              //this.loggerService.logInformation('postonsignalinit');
              this.authService.invoiceId.subscribe((invoceId: any) => {
                if (invoceId) {
                  this.navCtrl.navigateForward(['/tabs/alerts/repair-estimate', invoceId]);
                } else {
                  this.router.navigate(['tabs/home']);
                }
              });

              // this.router.navigate(['tabs/home']);
              this.setupDeeplinks();
            } else {
              this.router.navigate(['/splashscreen']);
            }
          }
        });
        this.setupDeeplinks();
        this.checkIsJobComplete();
      });
    } catch (error) {
      this.loggerService.logError(`initializeApp error: ${error.message}`);
    }
  }

  checkAndRedirect() {
    const currentUrl = window.location.href;
    if (currentUrl.includes('/buildquotelogin')) {
      this.router.navigate(['/buildquotelogin', {data: currentUrl}]);
    }
  }

  setupDeeplinks() {
    // Capacitor App plugin for handling deep links
    CapacitorApp.addListener('appUrlOpen', (event) => {
      try {
        const url = event.url;
        this.loggerService.logInformation('Deep link opened: ' + url);

        // Capture gclid/gbraid/wbraid for Google Ads attribution before
        // routing the link.
        this.adAttribution.captureFromUrl(url).catch(() => {});

        // Handle /buildquotelogin deep links
        if (url.includes('/buildquotelogin') || url.includes('buildquotelogin')) {
          // Extract query string from URL
          const urlParts = url.split('buildquotelogin');
          const queryString = urlParts[1] || '';
          this.router.navigate(['/buildquotelogin', {data: queryString}]);
        }
        // Handle /login/:id deep links
        else if (url.includes('/login/')) {
          const match = url.match(/\/login\/([^/?]+)/);
          if (match && match[1]) {
            const id = match[1];
            this.navCtrl.navigateForward(['/login', id]);
          }
        }
      } catch (error) {
        this.loggerService.logError(`Deep link error: ${error.message}`);
      }
    });
  }

  async checkIsJobComplete() {
    this.authService.consumerNotifications().subscribe(
      async (res: any) => {
        // this.loading = false;
        // this.app.dismissLoader();
        if (res?.data?.[0]?.job_status == '4' && res.data[0].read == '0') {
          const orderCompleted = await this.modalController.create({
            component: OrderCompletedComponent,
            cssClass: 'order-completed-modal',
            initialBreakpoint: 0.2,
            componentProps: {order: res.data[0]},
          });
          orderCompleted.present();
        }
        // this.notifications = res.data;
        // this.markReadNotification();
      },
      (error) => {
        // this.loading = false;
        // this.app.dismissLoader();
      }
    );
  }

  async openLink(link: string) {
    this.menu.close('first');

    if (link === 'shop') {
      await Browser.open({ url: 'https://automonkey.ourproshop.com/' });
    } else {
      const url = `https://theautomonkey.com/${link}`;
      await Browser.open({ url });
    }
  }
  async aboutAutomonkey() {
    this.menu.close('first');
    const url = `https://youtu.be/UZ50KHKsN7Y`;
    await Browser.open({ url });
  }

  notificationSettings() {
    this.menu.close('first');
    this.navCtrl.navigateForward('/tabs/profile/notification-settings');
  }
  appReview() {}

  shareApp() {}
  logout() {
    this.app.presentLoader();
    this.authService.logout().subscribe(
      (res: any) => {
        this.app.dismissLoader();
        this.storage.remove('is_verification_pending');
        this.authService.storage.remove('token');
        this.authService.storage.remove('newtoken');
        this.authService.storage.remove('user');
        this.authService.storage.remove('user_info');
        this.authService.storage.remove('MECHANIC_COUNT');
        this.authService.storage.remove('USER_GROUP');
        this.storage.remove('idget');
        this.storage.remove('IS_FROM_SERVEY');
        this.storage.remove('reduced_user');
        this.authService.invoiceId.next(null);
        localStorage.clear();

        this.authService.authenticationState.next(false);
        this.authService.authUser = undefined;
        this.menu.close('first');
        this.router.navigate(['/login']);
      },
      (error) => {
        this.app.dismissLoader();
      }
    );

    // this.authService.logout();
    // this.menu.close('first');
    // this.router.navigate(['/welcome']);
  }

  async deActivateAccount() {
    const alert = await this.alertCtrl.create({
      header: 'Are you sure, you want to deactivate your account?',
      buttons: [
        {
          text: 'No',
          role: 'cancel',
          handler: () => {
            this.handlerMessage = 'Alert canceled';
          },
        },
        {
          text: 'Yes',
          role: 'confirm',
          handler: () => {
            this.handlerMessage = 'Alert confirmed';
            this.storage.get('token').then((res) => {
              this.token = res;

              this.app.presentLoader();
              this.authService.deActivateAccount(this.token).subscribe(
                (res: any) => {
                  this.app.dismissLoader();
                  if (res && res.status) {
                    this.authService.storage.remove('token');
                    this.authService.storage.remove('newtoken');
                    this.app.presentToast(res.message);
                    this.step = 1;
                    this.menu.close('first');
                    this.navCtrl.navigateBack('login', {animated: false});
                  } else {
                    this.app.presentErrorToast(res.message);
                  }
                },
                (error) => {
                  this.app.dismissLoader();
                  this.app.presentErrorToast('Oops... Something went wrong. Please try again.');
                }
              );
            });
          },
        },
      ],
    });

    await alert.present();

    const {role} = await alert.onDidDismiss();
    this.roleMessage = `Dismissed with role: ${role}`;
  }

  backbtn() {
    this.platform.backButton.subscribe(async () => {
      if (this.router.isActive('/tabs/home', true)) {
        this.app.dismissLoader();
        navigator['app'].exitApp();
      } else {
        this.app.dismissLoader();
        this.location.back();
      }
    });
  }
}
